import React, { useEffect, useState } from "react";
import { AlertTriangle, X, Archive } from "lucide-react";
import { toast } from "sonner";
import { tourApi } from "@/lib/tourEntities";
import { apiError } from "@/lib/errors";

// Wave item 1 (ops-ux-documents-2026-09-23) — permanent-delete
// confirmation for tours. Super Admin only. Never rendered for
// other roles (ActionMenu gates the trigger via useAuth().isSuperAdmin
// and the server enforces 403 as defense in depth).
//
// Flow:
//   1. Modal opens → calls tourApi.deletePreflight(id).
//   2a. If hard_delete_allowed: shows the destructive button gated
//       behind a "type PERMANENTLY DELETE" text field.
//   2b. If !hard_delete_allowed: renders a blocking-deps panel with
//       the verbatim blocking_reasons from the API and an
//       "Archive instead" CTA that calls tourApi.archive.
//
// Never rendered when tour is null or open is false.
export const TourHardDeleteModal = ({
  open,
  tour,
  onCancel,
  onDeleted,
  onArchived,
}) => {
  const [preflight, setPreflight] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open || !tour?.id) return;
    setPreflight(null);
    setConfirmText("");
    setReason("");
    setErr(null);
    setBusy(false);
    (async () => {
      try {
        const p = await tourApi.deletePreflight(tour.id);
        setPreflight(p);
      } catch (e) {
        setErr(apiError(e));
      }
    })();
  }, [open, tour?.id]);

  if (!open || !tour) return null;

  const allowed = preflight?.hard_delete_allowed === true;
  const canSubmit = allowed && confirmText === "PERMANENTLY DELETE" && !busy;

  const runHardDelete = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await tourApi.hardDelete(tour.id, { reason: reason || undefined });
      toast.success(`Deleted permanently: ${tour.tour_number || tour.id}`);
      onDeleted?.(res);
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runArchiveInstead = async () => {
    setBusy(true);
    setErr(null);
    try {
      await tourApi.archive(tour.id);
      toast.success(`Archived ${tour.tour_number || tour.id}`);
      onArchived?.();
    } catch (e) {
      setErr(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      data-testid="tour-hard-delete-modal"
    >
      <div className="bg-white border border-critical-red rounded-md shadow-xl max-w-lg w-full">
        <div className="bg-critical-red text-white px-5 py-3 flex items-center gap-2">
          <AlertTriangle size={16} />
          <div className="text-[12px] uppercase tracking-widest font-semibold flex-1">
            Delete permanently
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="tour-hard-delete-close"
            className="text-white/80 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 text-[13px] space-y-3">
          <div>
            You are about to permanently delete{" "}
            <span className="font-semibold">
              {tour.tour_number || tour.id}
            </span>
            . This cannot be undone.
          </div>

          {preflight === null && !err && (
            <div className="text-steel" data-testid="tour-hard-delete-loading">
              Checking dependencies…
            </div>
          )}

          {err && (
            <div className="text-critical-red" data-testid="tour-hard-delete-error">
              {err}
            </div>
          )}

          {preflight && !allowed && (
            <div
              className="border border-amber-hold rounded-sm bg-amber-hold/10 p-3"
              data-testid="tour-hard-delete-blocked"
            >
              <div className="font-semibold text-carbon mb-2">
                Blocked — cannot permanently delete.
              </div>
              <div className="text-[12.5px] text-carbon mb-2">
                This tour has protected dependencies. Archive it instead so
                history is preserved.
              </div>
              <ul
                className="text-[12px] text-carbon list-disc pl-5 mb-3"
                data-testid="tour-hard-delete-blockers"
              >
                {(preflight.blocking_reasons || []).map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
              <button
                type="button"
                onClick={runArchiveInstead}
                disabled={busy}
                data-testid="tour-hard-delete-archive-instead"
                className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-coach-blue hover:bg-deep-road-navy text-white disabled:opacity-60 inline-flex items-center gap-2"
              >
                <Archive size={14} /> Archive instead
              </button>
            </div>
          )}

          {preflight && allowed && (
            <div className="space-y-3" data-testid="tour-hard-delete-allowed">
              <label className="block">
                <div className="text-[11px] uppercase tracking-widest text-steel mb-1">
                  Type "PERMANENTLY DELETE" to enable
                </div>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  data-testid="tour-hard-delete-confirm-input"
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-widest text-steel mb-1">
                  Reason (optional, audited)
                </div>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="tour-hard-delete-reason"
                  className="w-full border border-coach-silver rounded-sm px-2.5 py-1.5"
                />
              </label>
            </div>
          )}
        </div>
        <div className="border-t border-coach-silver px-5 py-3 flex justify-end gap-2 bg-road-white">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="tour-hard-delete-cancel"
            className="h-9 px-3 rounded-sm text-[12px] border border-coach-silver hover:bg-white"
          >
            Cancel
          </button>
          {preflight && allowed && (
            <button
              type="button"
              onClick={runHardDelete}
              disabled={!canSubmit}
              data-testid="tour-hard-delete-submit"
              className="h-9 px-4 rounded-sm text-[12.5px] font-semibold bg-critical-red hover:bg-red-800 text-white disabled:opacity-40"
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TourHardDeleteModal;
